import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Download, ArrowLeft, ExternalLink, ShieldCheck, Calculator } from "lucide-react";
import { toast } from "sonner";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import { API } from "../../../lib/api";

/**
 * Read-only public view of a saved RateLab calculation.
 *
 * Loaded via /ratelab/share/:shareId. Shows the same candidate-safe
 * Pay Package summary the PDF renders — never internal margin/profit.
 */

const CURRENCY = (n) =>
  isFinite(Number(n))
    ? Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
    : "—";

export default function RateLabShare() {
  const { shareId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    document.title = "Pay Package — Shared | TRITAL Care®";
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/ratelab-calculations/share/${encodeURIComponent(shareId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setState({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: String(e) });
      }
    })();
    return () => { alive = false; };
  }, [shareId]);

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/ratelab/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ share_id: shareId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TRITAL_PayPackage_${shareId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch {
      toast.error("PDF download failed.");
    }
  }

  if (state.loading) {
    return (
      <WorkforceOsLayout breadcrumb="Shared Pay Package" title="Loading…" eyebrow="RateLab™ Shared">
        <div className="py-20 flex items-center justify-center text-slate-500" data-testid="share-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> &nbsp;Loading shared calculation…
        </div>
      </WorkforceOsLayout>
    );
  }
  if (state.error || !state.data) {
    return (
      <WorkforceOsLayout breadcrumb="Shared Pay Package" title="Calculation not found" eyebrow="RateLab™ Shared">
        <div className="py-20 text-center" data-testid="share-not-found">
          <p className="text-slate-600">This shared calculation could not be found.</p>
          <Link to="/recruiter-os/ratelab" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold">
            <ArrowLeft className="w-4 h-4" /> Open RateLab
          </Link>
        </div>
      </WorkforceOsLayout>
    );
  }

  const d = state.data;
  const inputs = d.inputs || {};
  const gsa = d.gsa || {};

  const hours = parseFloat(inputs.guaranteedHours) || 0;
  const weeks = parseFloat(inputs.contractWeeks) || 0;
  const w2 = parseFloat(inputs.w2TaxableRate) || 0;
  const lodging = parseFloat(inputs.weeklyLodgingStipend) || 0;
  const mie = parseFloat(inputs.weeklyMieStipend) || 0;
  const taxableW2 = w2 * hours;
  const stipend = lodging + mie;
  const cliGross = taxableW2 + stipend;
  const blended = hours > 0 ? cliGross / hours : 0;
  const contractValue = cliGross * weeks;

  return (
    <WorkforceOsLayout
      breadcrumb="Shared Pay Package"
      title={d.candidate_name || "Pay Package"}
      description={`${inputs.role || ""}${inputs.role && inputs.specialty ? " · " : ""}${inputs.specialty || ""}`}
      eyebrow="RateLab™ Shared"
    >
      <section className="py-10" data-testid="share-page">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={downloadPdf}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold"
              data-testid="share-download-pdf-btn"
            >
              <Download className="w-4 h-4" /> Download Pay Package PDF
            </button>
            <Link
              to="/recruiter-os/ratelab"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold"
              data-testid="share-open-ratelab"
            >
              Open RateLab <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <span className="text-[12px] text-slate-500 ml-auto">Shared on {new Date(d.created_at).toLocaleString()}</span>
          </div>

          {/* Highlights */}
          <div className="rounded-2xl bg-trital-navy text-white p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-white/65">Candidate Weekly Gross</div>
              <div className="font-display font-black text-3xl tabular-nums mt-1" data-testid="share-cli-gross">{CURRENCY(cliGross)}</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-white/65">Blended Hourly Rate</div>
              <div className="font-display font-black text-3xl tabular-nums mt-1" data-testid="share-blended">{CURRENCY(blended)}/hr</div>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-white/65">Total Contract Value</div>
              <div className="font-display font-black text-3xl tabular-nums mt-1" data-testid="share-contract-value">{CURRENCY(contractValue)}</div>
            </div>
          </div>

          {/* Assignment block */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 overline text-slate-500"><Calculator className="w-3.5 h-3.5" /> ASSIGNMENT</div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13.5px]">
              {[
                ["Facility", inputs.facilityName],
                ["Location", [inputs.facilityCity, inputs.facilityState].filter(Boolean).join(", ")],
                ["ZIP", inputs.facilityZip],
                ["Contract Start", `${inputs.contractMonth || "—"} ${inputs.fiscalYear || ""}`.trim()],
                ["Contract Length", weeks ? `${weeks.toFixed(0)} weeks` : "—"],
                ["Guaranteed Hours/Wk", hours ? `${hours.toFixed(0)} hrs` : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-semibold text-slate-900 text-right">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Pay package block */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="overline text-slate-500">WEEKLY PAY PACKAGE</div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13.5px]">
              {[
                ["Taxable Hourly Rate (W2)", CURRENCY(w2)],
                ["Weekly Taxable Wages", CURRENCY(taxableW2)],
                ["Weekly Lodging Stipend", CURRENCY(lodging)],
                ["Weekly Meals & Incidentals", CURRENCY(mie)],
                ["Total Weekly Non-Taxable Stipend", CURRENCY(stipend)],
                inputs.overtimePayRate && ["Overtime Pay Rate", `${CURRENCY(inputs.overtimePayRate)}/hr`],
                inputs.holidayPayRate && ["Holiday Pay Rate", `${CURRENCY(inputs.holidayPayRate)}/hr`],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-semibold text-slate-900 text-right tabular-nums">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* GSA */}
          {gsa.weeklyMaxStipend ? (
            <div className="rounded-2xl border border-slate-200 bg-trital-medical-blue-soft p-6">
              <div className="overline text-trital-navy">GSA PER DIEM REFERENCE</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
                <div><div className="text-slate-500 text-[11px] uppercase">Source</div><div className="font-bold text-trital-navy">{gsa.sourceUsed}</div></div>
                <div><div className="text-slate-500 text-[11px] uppercase">Lodging/day</div><div className="font-bold tabular-nums">{CURRENCY(gsa.lodgingDaily)}</div></div>
                <div><div className="text-slate-500 text-[11px] uppercase">M&amp;IE/day</div><div className="font-bold tabular-nums">{CURRENCY(gsa.mieDaily)}</div></div>
                <div><div className="text-slate-500 text-[11px] uppercase">Weekly Max</div><div className="font-bold tabular-nums text-trital-navy">{CURRENCY(gsa.weeklyMaxStipend)}</div></div>
              </div>
            </div>
          ) : null}

          {/* Disclaimer */}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-[12px] text-slate-600 leading-relaxed flex gap-2" data-testid="share-disclaimer">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-trital-navy" />
            <p>
              This Pay Package summary is an estimate prepared by TRITAL Care® for staffing review.
              Final pay, stipend eligibility, and tax treatment are subject to client contract,
              payroll verification, traveler qualification, and tax home confirmation. Non-taxable
              stipends require an established tax home and qualifying travel.
            </p>
          </div>
        </div>
      </section>
    </WorkforceOsLayout>
  );
}
